import { type ReviewChecklistItem } from "@mg/schemas/src/christo/askPuntt";
import {
  CaretDown,
  CheckCircle,
  CircleNotch,
  ClockCountdown,
} from "@phosphor-icons/react";
import { Flex, Text } from "@radix-ui/themes";
import cx from "classnames";
import { useEffect, useRef, useState } from "react";

import { TOOL_CHIP_SIZING } from "./AgentSteps";

import { interactiveSurfaceClasses } from "../../../components/InteractiveSurface";

interface ChecklistItem {
  id: string;
  label: string;
  sub: string;
  ticketLabel?: string;
}

// Canonical per-ticket checks used for project (multi-ticket) reviews where
// every ticket runs through the same set of checks in parallel.
export const TICKET_REVIEW_CHECKS: { label: string; sub: string }[] = [
  {
    label: "Brand Guidelines",
    sub: "Visual standards, identity, tone of voice",
  },
  {
    label: "Visual Quality",
    sub: "Composition, imagery, typography, design",
  },
  {
    label: "Content Review",
    sub: "Copy, claims, messaging, accuracy",
  },
  {
    label: "Technical Specifications",
    sub: "File format, resolution, print requirements",
  },
];

const CHECKLIST_BY_WORKFLOW: Record<string, ChecklistItem[]> = {
  artwork_review: [
    {
      id: "file_build",
      label: "File Build",
      sub: "Dielines, fonts, colors, metadata, production setup",
    },
    {
      id: "design_layout",
      label: "Design & Layout",
      sub: "Logo, brand guidelines, imagery, barcode, print readiness",
    },
    {
      id: "content_claims",
      label: "Content & Claims",
      sub: "Ingredients, nutrition facts, regulatory text, disclaimers",
    },
    {
      id: "master_comparison",
      label: "Master Comparison",
      sub: "Alignment to reference spec, dimensions, copy accuracy",
    },
  ],
  compliance_review: [
    {
      id: "regulatory",
      label: "Regulatory Compliance",
      sub: "EU/UK regulations, permitted health and nutrition claims",
    },
    {
      id: "claim_accuracy",
      label: "Claim Accuracy",
      sub: "Evidence base, substantiation requirements",
    },
    {
      id: "market_rules",
      label: "Market-specific Rules",
      sub: "Regional and country-level requirements",
    },
    {
      id: "enforcement",
      label: "Enforcement Risk",
      sub: "ASA, FSA, and regulator precedent cases",
    },
  ],
  compare: [
    {
      id: "design_align",
      label: "Design Alignment",
      sub: "Layout, colors, typography, visual hierarchy",
    },
    {
      id: "content_match",
      label: "Content Accuracy",
      sub: "Copy, claims, specifications against reference",
    },
    {
      id: "brand_consistency",
      label: "Brand Consistency",
      sub: "Logo usage, color palette, brand standards",
    },
    {
      id: "spec_compliance",
      label: "Specification Compliance",
      sub: "Dimensions, materials, technical requirements",
    },
  ],
  review: [
    {
      id: "brand",
      label: "Brand Guidelines",
      sub: "Visual standards, identity, tone of voice",
    },
    {
      id: "visual_quality",
      label: "Visual Quality",
      sub: "Composition, imagery, typography, design",
    },
    {
      id: "content",
      label: "Content Review",
      sub: "Copy, claims, messaging, accuracy",
    },
    {
      id: "technical",
      label: "Technical Specifications",
      sub: "File format, resolution, print requirements",
    },
  ],
};

const FALLBACK_CHECKLIST: ChecklistItem[] = [
  {
    id: "analysis",
    label: "Analysing files",
    sub: "Reading and processing uploaded assets",
  },
  {
    id: "review",
    label: "Running review",
    sub: "Applying knowledge base and guidelines",
  },
  {
    id: "findings",
    label: "Compiling findings",
    sub: "Generating structured feedback and results",
  },
];

type DynamicChecklistItem = ReviewChecklistItem & { ticketLabel?: string };

function toChecklistItems(
  dynamic: DynamicChecklistItem[] | undefined,
  workflowType: string,
): ChecklistItem[] {
  if (dynamic && dynamic.length > 0) {
    return dynamic.map((item, idx) => ({
      id: `dynamic_${idx}`,
      label: item.label,
      sub: item.sub,
      ticketLabel: item.ticketLabel,
    }));
  }
  return CHECKLIST_BY_WORKFLOW[workflowType] ?? FALLBACK_CHECKLIST;
}

interface ReviewChecklistProps {
  workflowType: string;
  isReviewPolling?: boolean;
  reviewComplete?: boolean;
  dynamicItems?: DynamicChecklistItem[];
  /**
   * When set, render in parallel-review mode: header reads "Reviewing N
   * tickets in parallel", items rotate via a sliding window of the most
   * recent reveals, and per-item completion follows reviewComplete only
   * (since the checks here represent in-flight reviews, not phases that
   * already finished by the time polling starts).
   */
  parallelTicketCount?: number;
}

const PARALLEL_VISIBLE_WINDOW = 5;

export function ReviewChecklist({
  workflowType,
  isReviewPolling = false,
  reviewComplete = false,
  dynamicItems,
  parallelTicketCount,
}: ReviewChecklistProps) {
  const items = toChecklistItems(dynamicItems, workflowType);
  const isParallelMode =
    typeof parallelTicketCount === "number" && parallelTicketCount > 0;

  const [widgetOpen, setWidgetOpen] = useState(true);
  const [expandedIds, setExpandedIds] = useState<Set<string>>(new Set());

  const hasDynamic = !!(dynamicItems && dynamicItems.length > 0);
  const staggerKey = `${hasDynamic ? "dyn" : "fallback"}-${items.length}`;

  // Refs survive remounts. Store the wall-clock start time and pre-computed
  // cumulative delays so a component remount (waitingForAI→false moves the
  // element in the DOM) can resume exactly where the timer left off.
  const startTimeRef = useRef<number | null>(null);
  const staggerDelaysRef = useRef<number[]>([]);
  const prevStaggerKeyRef = useRef("");

  if (prevStaggerKeyRef.current !== staggerKey && !reviewComplete) {
    prevStaggerKeyRef.current = staggerKey;
    startTimeRef.current = Date.now();
    const delays: number[] = [0];
    for (let i = 1; i < items.length; i++) {
      delays.push(delays[i - 1] + 3000 + Math.random() * 4000);
    }
    staggerDelaysRef.current = delays;
  }

  // On remount, initialize visibleCount to however many items have already
  // elapsed — so we never flash backwards or pre-fill the box.
  const [visibleCount, setVisibleCount] = useState<number>(() => {
    if (reviewComplete) return items.length;
    const start = startTimeRef.current;
    if (start === null) return 0;
    const elapsed = Date.now() - start;
    return staggerDelaysRef.current.filter((d) => d <= elapsed).length;
  });

  // Collapse the widget automatically when the review finishes.
  useEffect(() => {
    if (reviewComplete) setWidgetOpen(false);
  }, [reviewComplete]);

  useEffect(() => {
    if (reviewComplete) {
      setVisibleCount(items.length);
      return;
    }

    // Recompute how many items should be visible right now (handles remounts).
    const start = startTimeRef.current ?? Date.now();
    const delays = staggerDelaysRef.current;
    const nowCount = delays.filter((d) => start + d <= Date.now()).length;
    setVisibleCount(nowCount);

    let count = nowCount;
    let timeoutId: ReturnType<typeof setTimeout>;

    const scheduleNext = () => {
      if (count >= items.length) return;
      const remaining = start + delays[count] - Date.now();
      timeoutId = setTimeout(
        () => {
          count++;
          setVisibleCount(count);
          scheduleNext();
        },
        Math.max(0, remaining),
      );
    };

    scheduleNext();
    return () => clearTimeout(timeoutId);
  }, [staggerKey, reviewComplete, items.length]);

  const toggleItem = (itemId: string) => {
    setExpandedIds((prev) => {
      const next = new Set(prev);
      if (next.has(itemId)) next.delete(itemId);
      else next.add(itemId);
      return next;
    });
  };

  // In parallel mode the items represent in-flight per-ticket reviews, so a
  // green check would be misleading until the whole project review completes.
  // For single-ticket flows the existing semantics still apply: items are
  // conceptual phases that are done by the time polling starts.
  const done = isParallelMode
    ? reviewComplete
    : isReviewPolling || reviewComplete;
  const visibleItems = items.slice(0, visibleCount);

  const MAX_COLLAPSED = 5;
  const [showAll, setShowAll] = useState(false);
  // Both modes show a rolling window of the most recent reveals so the latest
  // items always sit at the bottom and earlier items scroll off the top.
  // Single-ticket mode also exposes a "Show all" affordance so users can pop
  // open the full list once the reveal stream has gone past.
  const windowSize = isParallelMode ? PARALLEL_VISIBLE_WINDOW : MAX_COLLAPSED;
  const displayedItems =
    !isParallelMode && showAll
      ? visibleItems
      : visibleItems.slice(Math.max(0, visibleItems.length - windowSize));
  const displayWindowOffset =
    !isParallelMode && showAll
      ? 0
      : Math.max(0, visibleItems.length - windowSize);
  const hiddenEarlierCount =
    !isParallelMode && !showAll
      ? Math.max(0, visibleItems.length - MAX_COLLAPSED)
      : 0;
  const hasMore = !isParallelMode && visibleItems.length > MAX_COLLAPSED;

  return (
    <>
      {/* Shadow on outer wrapper; inner clips content to rounded rect */}
      <div className="rounded-xl shadow-sm">
        <div
          data-testid="review-checklist"
          className={cx(
            "overflow-hidden",
            interactiveSurfaceClasses({ expandable: true }),
          )}
        >
          {/* Header — click to collapse/expand the whole widget */}
          <button
            data-testid="review-checklist-toggle"
            aria-label="Toggle review checklist visibility"
            type="button"
            onClick={() => setWidgetOpen((p) => !p)}
            className="flex w-full items-center gap-2 bg-base-white px-2.5 py-1.5 text-left outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-puntt-accent-7"
          >
            {isReviewPolling ? (
              <span className="size-2.5 shrink-0 animate-pulse rounded-full bg-puntt-accent-9" />
            ) : reviewComplete ? (
              <CheckCircle
                size={16}
                weight="fill"
                className="text-malachite-600"
              />
            ) : (
              <CircleNotch
                size={16}
                className="animate-spin text-puntt-accent-9 motion-reduce:animate-none"
              />
            )}
            <Text
              size="2"
              weight="medium"
              className="text-puntt-neutral-gray-12"
            >
              {reviewComplete
                ? "Review complete"
                : isParallelMode
                  ? parallelTicketCount === 1
                    ? "Reviewing your asset"
                    : `Reviewing ${parallelTicketCount} assets in parallel`
                  : isReviewPolling
                    ? "Review in progress"
                    : "Reviewing your files"}
            </Text>
            {isReviewPolling && (
              <Flex align="center" gap="1" className="mr-1">
                <ClockCountdown
                  size={12}
                  className="text-puntt-neutral-gray-9"
                />
                <Text size="1" className="text-puntt-neutral-gray-9">
                  Results on ticket
                </Text>
              </Flex>
            )}
            <CaretDown
              size={14}
              className={`ml-auto shrink-0 text-puntt-neutral-gray-9 transition-transform duration-200 ${widgetOpen ? "rotate-0" : "-rotate-90"}`}
            />
          </button>

          {/* Items — only render what's visible so the box grows as items appear */}
          {widgetOpen && displayedItems.length > 0 && (
            <div className="border-t border-puntt-neutral-gray-6 px-2.5 py-1.5">
              <div className="flex flex-col gap-1.5">
                {hasMore && hiddenEarlierCount > 0 && (
                  <button
                    data-testid="review-checklist-show-earlier"
                    aria-label="Show earlier review checklist items"
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      setShowAll(true);
                    }}
                    className="w-full rounded-lg bg-puntt-neutral-gray-2 px-2.5 py-1.5 text-left hover:bg-puntt-neutral-gray-3"
                  >
                    <Text size="1" className="text-puntt-neutral-gray-9">
                      Show {hiddenEarlierCount} earlier
                    </Text>
                  </button>
                )}
                {displayedItems.map((item, idx) => {
                  const absoluteIdx = displayWindowOffset + idx;
                  const isLatestEntry =
                    absoluteIdx === visibleItems.length - 1 && !reviewComplete;
                  const itemExpanded = expandedIds.has(item.id);

                  return (
                    <div
                      key={item.id}
                      className={`overflow-hidden rounded-lg border border-puntt-neutral-gray-6 bg-puntt-neutral-gray-2 ${isLatestEntry ? "animate-block-fade-up" : ""}`}
                    >
                      <button
                        data-testid="review-checklist-item"
                        aria-label={`Toggle details for ${item.label}`}
                        type="button"
                        onClick={() => toggleItem(item.id)}
                        className={`flex w-full items-center ${TOOL_CHIP_SIZING} text-left transition-colors hover:bg-puntt-neutral-gray-3/90 focus-visible:bg-puntt-neutral-gray-3 focus-visible:outline-none`}
                      >
                        {done ? (
                          <CheckCircle
                            size={16}
                            weight="fill"
                            className="mt-0.5 shrink-0 text-malachite-600"
                          />
                        ) : (
                          <CircleNotch
                            size={16}
                            className="mt-0.5 shrink-0 animate-spin text-puntt-accent-9 motion-reduce:animate-none"
                            style={{ animationDuration: `${1.5 + idx * 0.3}s` }}
                          />
                        )}
                        <Flex
                          direction="column"
                          align="start"
                          className="flex-1"
                        >
                          {item.ticketLabel && (
                            <Text
                              size="1"
                              className="text-left text-puntt-neutral-gray-9"
                            >
                              {item.ticketLabel}
                            </Text>
                          )}
                          <Text
                            size="1"
                            weight="medium"
                            className={`text-left ${
                              done
                                ? "text-puntt-neutral-gray-11"
                                : "text-puntt-neutral-gray-12"
                            }`}
                          >
                            {item.label}
                          </Text>
                        </Flex>
                        <CaretDown
                          size={12}
                          className={`shrink-0 text-puntt-neutral-gray-7 transition-transform duration-150 ${itemExpanded ? "rotate-0" : "-rotate-90"}`}
                        />
                      </button>

                      {itemExpanded && (
                        <div className="border-t border-puntt-neutral-gray-6 bg-base-white p-3 pl-[2.375rem]">
                          <Text
                            as="p"
                            size="2"
                            className="leading-relaxed text-puntt-neutral-gray-12"
                          >
                            {item.sub}
                          </Text>
                        </div>
                      )}
                    </div>
                  );
                })}

                {hasMore && showAll && (
                  <button
                    data-testid="review-checklist-show-less"
                    aria-label="Show fewer review checklist items"
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      setShowAll(false);
                    }}
                    className="w-full rounded-lg bg-puntt-neutral-gray-2 px-2.5 py-1.5 text-left hover:bg-puntt-neutral-gray-3"
                  >
                    <Text size="1" className="text-puntt-neutral-gray-9">
                      Show less
                    </Text>
                  </button>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </>
  );
}
